/**
 * PlanChangerWrapper - Entry point for subscription plan changes.
 *
 * This wrapper component initializes the Stripe.js SDK for existing subscribers
 * who want to upgrade or downgrade their plan. Stripe context is required because
 * upgrades may require 3D Secure authentication for the prorated payment.
 *
 * Plan Change Scenarios:
 *
 * 1. Upgrade (e.g., Plus → Pro, Pro → Enterprise):
 *    - Calculates prorated charge for remaining billing period
 *    - Charges card immediately via Stripe (may trigger 3DS)
 *    - Immediately upgrades user to new plan features
 *
 * 2. Downgrade (e.g., Pro → Plus, Plus → Free):
 *    - No immediate charge
 *    - User keeps current plan features until period_end
 *    - Next billing cycle starts at new (lower) price
 *
 * Plan Codes (matching User.source_plans):
 *   - 0: FREE (Basic)
 *   - 1: PAID (Plus) - retired but still used for downgrades
 *   - 2: PR_TEAM (Pro)
 *   - 3: CLIENT_FIRM (Enterprise)
 *
 * Component Lifecycle:
 *   - Same as StripeFormWrapper - reloads page on unmount to cleanup Stripe iframe
 *
 * Used by:
 *   - billing_accounts/edit_plan.html.erb
 *
 * @see BillingPlanChanger for the plan change UI and API interaction
 * @see Api::Internal::BillingAccountsController#update_plan for backend
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import BillingPlanChanger from './_billing_plan_changer';

class PlanChangerWrapper extends React.Component {
  STRIPE_PROMISE = loadStripe(this.props.stripePublishableKey);

  constructor(props) {
    super(props);
    this.state = { wasSuccessful: false };
  }

  componentWillUnmount() {
    const { wasSuccessful } = this.state;
    if (!wasSuccessful) {
      // This is an intentional location reload meant to unload the Stripe
      // iframe. There's no need to keep Stripe loaded in memory once we
      // leave the payment or payment method update page:
      // https://github.com/stripe/react-stripe-elements/issues/99
      window.location.reload();
    }
  }

  handleSuccess = () => {
    this.setState({ wasSuccessful: true });
  };

  render() {
    const {
      accountCode,
      newSourcePlan,
      oldSourcePlan,
      oldPlanPitches,
      oldPlanUnlimited,
      oldPlanDiscontinued,
      willLoseDiscount,
      nextBillDateString,
      newMonthlyChargeString,
      successUrl,
      interval,
    } = this.props;

    return (
      <Elements stripe={this.STRIPE_PROMISE}>
        <BillingPlanChanger
          accountCode={accountCode}
          newSourcePlan={newSourcePlan}
          oldSourcePlan={oldSourcePlan}
          oldPlanPitches={oldPlanPitches}
          oldPlanUnlimited={oldPlanUnlimited}
          oldPlanDiscontinued={oldPlanDiscontinued}
          willLoseDiscount={willLoseDiscount}
          nextBillDateString={nextBillDateString}
          newMonthlyChargeString={newMonthlyChargeString}
          successUrl={successUrl}
          onSuccess={this.handleSuccess}
          interval={interval}
        />
      </Elements>
    );
  }
}

PlanChangerWrapper.propTypes = {
  stripePublishableKey: PropTypes.string.isRequired, // Stripe public key to pass to the loadStripe method
  accountCode: PropTypes.number.isRequired,
  newSourcePlan: PropTypes.number.isRequired,
  oldSourcePlan: PropTypes.number.isRequired,
  oldPlanPitches: PropTypes.number.isRequired,
  oldPlanUnlimited: PropTypes.bool,
  oldPlanDiscontinued: PropTypes.bool,
  willLoseDiscount: PropTypes.bool,
  nextBillDateString: PropTypes.string.isRequired,
  newMonthlyChargeString: PropTypes.string.isRequired,
  successUrl: PropTypes.string.isRequired,
  interval: PropTypes.string,
};

PlanChangerWrapper.defaultProps = {
  oldPlanUnlimited: false,
  oldPlanDiscontinued: false,
  willLoseDiscount: false,
  interval: 'month',
};

export default PlanChangerWrapper;
